import type { JourneyPage } from './types'

export const headcountToHeadroom: JourneyPage = {
  slug: 'headcount-to-headroom',
  intro: [
    { text: 'You need ', highlight: 'another paraplanner' },
    { text: 'But good ones are ', highlight: 'hard to find' },
    { text: 'Your team works ', highlight: 'evenings and weekends' },
    { text: 'What if you could create capacity ', highlight: 'without hiring' },
    { text: 'And reclaim your ', highlight: 'life' },
    { text: 'This is ', highlight: 'WealthSpace' },
  ],
  colorClass: 'headroom',
  seo: {
    title: 'WealthSpace Headcount to Headroom | Create Capacity Without Hiring',
    description:
      'Create capacity without adding staff. AI automation that gives your team headroom to grow - no recruitment, no outsourcing, no compromise on quality.',
    keywords:
      'paraplanner capacity, financial adviser productivity, practice growth, adviser efficiency, reduce admin, scale advice firm, paraplanner shortage, outsourcing alternative',
  },
  hero: {
    eyebrow: 'The capacity crisis, solved',
    title: 'From headcount to',
    titleAccent: 'headroom',
    subtitle:
      'Good paraplanners are hard to find. Outsourcing is expensive and unpredictable. But your team is drowning in admin. Theres another way.',
    microcopy: 'No recruitment. No outsourcing. Just capacity.',
  },
  journeySection: {
    label: 'The Story',
    title: 'Create capacity',
    titleAccent: 'without hiring',
    subtitle: 'See how firms are scaling without the recruitment headache.',
  },
  timeline: [
    {
      time: 'The situation today',
      icon: 'clock',
      iconColor: 'amber',
      title: 'The capacity crisis',
      meta: 'Drowning in admin',
      body: "Your paraplanner is working late every night. There's a growing backlog of reports. New client enquiries are being turned away. You've been searching for another paraplanner for months, but the good ones are hard to find - and when you do, the salary expectations are eye-watering.",
      previewLabel: 'The problem',
      previewText:
        '6+ months to hire an experienced paraplanner. £45-60k salary expectations. £300+ per report for quality outsourced work.',
    },
    {
      time: 'Week 1 with WealthSpace',
      icon: 'sparkle',
      iconColor: 'teal',
      title: 'The switch flips',
      meta: '3+ hours saved per report',
      body: 'Your first client meeting with WealthSpace. Click record and focus on the conversation - actually listen, rather than scribbling notes. An hour later, a notification appears: transcription complete, file note ready, draft report in progress.',
      previewLabel: 'What changes',
      previewText:
        'Suitability reports: from 4-5 hours to 45 minutes of review. File notes: instant and structured. Meeting prep: research compiled automatically.',
    },
    {
      time: 'Week 4',
      icon: 'document',
      iconColor: 'teal',
      title: 'The backlog clears',
      meta: 'Same-day reports',
      body: "Every meeting now produces an instant file note and draft report. Your paraplanner's role shifts from typing to reviewing and refining. The backlog disappears. Reports go out on the same day as meetings.",
      previewLabel: 'The new normal',
      previewText:
        'We used to be weeks behind on reports. Now they go out the same day. Clients are genuinely impressed.',
    },
    {
      time: 'Month 2',
      icon: 'users',
      iconColor: 'emerald',
      title: 'Your team evolves',
      meta: 'From typing to reviewing',
      body: "Your paraplanner isn't made redundant - they're elevated. Instead of typing notes and drafting from scratch, they're reviewing AI outputs, catching edge cases, and handling complex technical research. Work that actually uses their qualifications.",
      previewLabel: 'The transformation',
      previewText:
        'From typing to reviewing and refining. From admin to technical expertise. From burnt out to energised.',
    },
    {
      time: 'Month 6',
      icon: 'chart',
      iconColor: 'emerald',
      title: 'Growth unlocked',
      meta: '40% more capacity',
      body: "You're saying yes to new clients again. The same team is handling significantly more clients than before. You've stopped searching for that second paraplanner. You don't need one.",
      previewLabel: 'The result',
      previewText:
        "More client capacity from the same team. No new hires and no outsourcing invoices. The practice is growing, but it doesn't feel harder.",
    },
  ],
  result: {
    title: 'Headcount stayed the same.',
    titleAccent: 'Capacity grew.',
    body: 'No recruitment agencies. No job ads. No interviews. No training period. No outsourcing invoices. No more Sunday dread. Just the same team, doing more meaningful work, serving more clients - and getting their lives back.',
    stats: [
      { value: 'Capacity', label: 'Created from the team you already have' },
      { value: 'No', label: 'Recruitment or outsourcing required' },
      { value: 'Weekends', label: 'Back for the people who matter' },
    ],
  },
  features: {
    label: 'Where the time goes',
    title: 'Automation that',
    titleAccent: 'actually works',
    subtitle:
      'Not busy-work automation. Real capacity gains that transform how your team works.',
    items: [
      {
        icon: 'document',
        title: 'Report generation',
        body: 'From 4-5 hours to 45 minutes. AI generates first drafts from meeting notes. Your team reviews and approves.',
      },
      {
        icon: 'mic',
        title: 'Meeting capture',
        body: 'Automatic transcription, notes, and actions. Zero admin after meetings. Walk straight to your next one.',
      },
      {
        icon: 'clock',
        title: 'Task automation',
        body: 'Tasks created automatically from meetings and client interactions. Calendar blocks added. Nothing forgotten.',
      },
    ],
  },
  testimonial: {
    quote:
      'We built this because we lived it. The impossible search for qualified paraplanners. The eye-watering outsourcing quotes. The feeling of turning away good clients because you simply didnt have the capacity. There had to be a better way - and now there is.',
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'Stop searching. Start',
    titleAccent: 'scaling.',
    subtitle:
      "See how much capacity you could unlock with your existing team. Book a demo and we'll show you the numbers for your firm.",
    note: 'Calculate your ROI in the demo. No commitment required.',
  },
}
